// F-WEB-019 /admin/tags — CRUD via DataTable (tabela desktop / cartoes <48rem).
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);
}

import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type { Tag } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type DataTableColumn } from "../../components/ui/DataTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";

export function AdminTagsPage(): JSX.Element {
  const qc = useQueryClient();
  const { data: tags = [], isLoading, isError, error } = useQuery<Tag[]>({
    queryKey: ["admin-tags"],
    queryFn: async () => (await api.get<Tag[]>("/api/tags")).data,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);

  const delMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/tags/${id}`);
    },
    onSuccess: () => {
      toast.success("Tag excluída");
      qc.invalidateQueries({ queryKey: ["admin-tags"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const onEdit = (t: Tag) => setEditing(t);
  const onDelete = (t: Tag) => {
    if (confirm(`Excluir tag "${t.name}"?`)) delMut.mutate(t.id);
  };

  const columns: DataTableColumn<Tag>[] = [
    { key: "name", label: "Nome", width: "minmax(10rem,3fr)", render: (t) => <span className="row__title truncate">{t.name}</span> },
    { key: "slug", label: "Slug", width: "minmax(7rem,2fr)", mono: true, render: (t) => t.slug_wc ?? "—" },
    {
      key: "usage",
      label: "Uso",
      width: "minmax(4rem,1fr)",
      render: (t) => <span className="chip chip--mono">{t.usage_count ?? 0}</span>,
    },
    {
      key: "actions",
      label: "",
      width: "minmax(9rem,auto)",
      render: (t) => (
        <span className="cluster" style={{ justifyContent: "flex-end", gap: ".375rem" }}>
          <Button variant="ghost" size="sm" onClick={() => onEdit(t)}>
            Editar
          </Button>
          <span aria-hidden style={{ color: "var(--color-ink-2)" }}>·</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(t)}
            disabled={delMut.isPending}
            aria-label={`Excluir tag ${t.name}`}
          >
            Excluir
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Tags"
        description="Tags canônicas reutilizadas em entries e overrides por canal."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="icon" aria-hidden /> Nova tag
          </Button>
        }
      />

      {isLoading ? (
        <Card>
          <CardContent className="stack">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      ) : isError ? (
        <EmptyState title="Erro" description={apiErrorMessage(error)} />
      ) : (
        <DataTable
          columns={columns}
          rows={tags}
          rowKey={(t) => t.id}
          emptyState={<EmptyState title="Nenhuma tag cadastrada" />}
          renderCard={(t) => (
            <div className="row-card">
              <div className="row-card__body">
                <span className="row__title truncate">{t.name}</span>
                <div className="cluster">
                  <span className="chip chip--mono">{t.slug_wc ?? "—"}</span>
                  <span className="chip chip--mono">uso: {t.usage_count ?? 0}</span>
                </div>
                <div className="cluster">
                  <Button variant="secondary" size="sm" onClick={() => onEdit(t)}>
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(t)}
                    disabled={delMut.isPending}
                    aria-label="Excluir tag"
                  >
                    <Trash2 className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden />
                  </Button>
                </div>
              </div>
            </div>
          )}
        />
      )}

      <TagDialog
        key={editing?.id ?? "new"}
        open={createOpen || editing !== null}
        editing={editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function TagDialog({
  open,
  editing,
  onOpenChange,
}: {
  open: boolean;
  editing: Tag | null;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const isEdit = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [slug, setSlug] = useState(editing?.slug_wc ?? "");
  const [slugTouched, setSlugTouched] = useState<boolean>(
    isEdit && editing ? (editing.slug_wc ?? "") !== slugify(editing.name ?? "") : false,
  );

  const mut = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name };
      if (slug && slug.trim()) body.slug_wc = slug.trim();
      if (isEdit && editing) {
        await api.patch(`/api/tags/${editing.id}`, body);
      } else {
        await api.post("/api/tags", body);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? "Tag atualizada" : "Tag criada");
      qc.invalidateQueries({ queryKey: ["admin-tags"] });
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={editing?.id ?? "new"}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar tag" : "Nova tag"}</DialogTitle>
          <DialogDescription>
            Tags são canônicas. Excluir bloqueia 409 se em uso (RN08).
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
          className="stack"
        >
          <div className="field">
            <Label htmlFor="tag-name">Nome</Label>
            <Input
              id="tag-name"
              value={name}
              onChange={(e) => {
                const v = e.target.value;
                setName(v);
                if (!slugTouched) setSlug(slugify(v));
              }}
              required
            />
          </div>
          <div className="field">
            <Label htmlFor="tag-slug">Slug (opcional)</Label>
            <Input
              id="tag-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              placeholder="tag-exemplo"
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={mut.isPending}>
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
